import type React from "react";

import {ToolCallStatus} from "@copilotkit/react-core/v2";
import {
  Button,
  ChatTool,
  Disclosure,
  TextShimmer,
  Tooltip,
  Typography,
} from "@project/design-system-web";
import {
  ArrowUpRightFromSquareIcon,
  CheckIcon,
  ChevronsCollapseVerticalIcon,
  ChevronsExpandVerticalIcon,
  XmarkIcon,
} from "@project/icons-web";
import {useEffect, useId, useState} from "react";

import {toToolPartState} from "../adapters/tool-status-adapter";
import {aiAssistantMessages} from "../ai-assistant.messages";
import {aiStore} from "../state/ai-store";

export type ToolCallSeverity = "success" | "warning" | "danger";

export interface ToolCallAction {
  label: string;
  onPress: () => void;
}

export interface ToolCallApproval {
  approveLabel?: React.ReactNode;
  onApprove: () => void;
  onReject?: () => void;
  rejectLabel?: React.ReactNode;
}

export interface ToolCallNavigationAction {
  hideWhenSuccessful?: boolean;
  label: string;
  onPress: () => void;
}

export interface ToolCallWrapperProps {
  /** Extra content rendered after the expanded tool body, usually follow-up options. */
  actions?: ToolCallAction[];
  /** Pending human approval controls. Pending approvals are forced expanded and non-collapsible. */
  approval?: ToolCallApproval;
  /** The content of the tool call. Domain tools should render user-facing summaries here. */
  children?: React.ReactNode;
  /** Whether the body should be expanded by default (if children exist) */
  defaultExpanded?: boolean;
  /** Technical failure details. User rejection/cancel should use resultLabel + severity instead. */
  errorText?: string;
  /** The icon representing the tool */
  icon?: React.ReactNode;
  /** Controlled expanded state. Pending approvals are always forced open. */
  isExpanded?: boolean;
  /** The user-facing tool name (e.g. "Generate Report") */
  name: React.ReactNode;
  /** Optional action that opens the related page, entity, or artifact. */
  navigationAction?: ToolCallNavigationAction;
  /** Controlled expansion callback. Not called while a pending approval is locked open. */
  onExpandedChange?: (isExpanded: boolean) => void;
  /** Human-readable terminal/business result label, e.g. "Approved", "Rejected", "Found 3 results". */
  resultLabel?: React.ReactNode;
  /** Terminal visual severity. Technical errors should still provide errorText. */
  severity?: ToolCallSeverity;
  /** The CopilotKit execution status. */
  status?: ToolCallStatus;
  /** Optional title for the specific action (e.g. "Q3 Financials") */
  title?: React.ReactNode;
}

const StatusDot = ({severity}: {severity: ToolCallSeverity}) => {
  switch (severity) {
    case "success":
      return <div className="bg-success size-2 rounded-full" />;
    case "warning":
      return <div className="bg-warning size-2 animate-pulse rounded-full" />;
    case "danger":
      return <div className="bg-danger size-2 rounded-full" />;
  }
};

function getDerivedSeverity(heroState: ReturnType<typeof toToolPartState>): ToolCallSeverity {
  if (heroState === "output-error") return "danger";
  if (
    heroState === "input-streaming" ||
    heroState === "input-available" ||
    heroState === "requires-action"
  ) {
    return "warning";
  }

  return "success";
}

function debugM4A(message: string, payload?: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  console.log(`[DEBUG-M4A] ${message}`, payload ?? {});
}

export function ToolCallWrapper({
  actions,
  approval,
  children,
  defaultExpanded = false,
  errorText,
  icon,
  isExpanded,
  name,
  navigationAction,
  onExpandedChange,
  resultLabel,
  severity,
  status,
  title,
}: ToolCallWrapperProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isNavigationActionHovered, setIsNavigationActionHovered] = useState(false);
  const generatedId = useId();
  const isPendingApproval = status === ToolCallStatus.Executing && Boolean(approval);
  const heroState = toToolPartState({
    hasError: Boolean(errorText),
    isApprovalPending: isPendingApproval,
    severity,
    status,
  });
  const derivedSeverity = getDerivedSeverity(heroState);
  const shouldHideNavigationAction =
    navigationAction?.hideWhenSuccessful &&
    status === ToolCallStatus.Complete &&
    derivedSeverity === "success";
  const hasActions = Boolean(status === ToolCallStatus.Complete && actions?.length);
  const isExpandable = Boolean(children) || hasActions || isPendingApproval || Boolean(errorText);
  const canToggleDisclosure = isExpandable && !isPendingApproval;
  const shouldShowDisclosureAffordance =
    canToggleDisclosure && isHeaderHovered && !isNavigationActionHovered;
  const shouldShimmerToolLabel = heroState === "input-streaming" || heroState === "input-available";
  const resolvedExpanded = isPendingApproval ? true : (isExpanded ?? internalExpanded);

  useEffect(() => {
    const blockerId = `chat-approval-${generatedId}`;

    if (!isPendingApproval) {
      debugM4A("chat blocker unregister", {blockerId, name});
      aiStore.state.unregisterConversationBlocker(blockerId);

      return;
    }

    debugM4A("chat blocker register", {blockerId, name});
    aiStore.state.registerConversationBlocker({
      id: blockerId,
      kind: "chat-approval",
      label: aiAssistantMessages.actionRequired.defaultMessage,
    });

    return () => {
      debugM4A("chat blocker cleanup", {blockerId, name});
      aiStore.state.unregisterConversationBlocker(blockerId);
    };
  }, [generatedId, isPendingApproval]);

  const handleExpandedChange = (nextExpanded: boolean) => {
    if (isPendingApproval) return;

    setInternalExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  const toggleDisclosure = () => {
    if (!canToggleDisclosure) return;

    handleExpandedChange(!resolvedExpanded);
  };

  const handleHeaderClick = (event: React.MouseEvent) => {
    if (!canToggleDisclosure) return;

    const target = event.target as HTMLElement;

    if (
      target.closest(
        '[data-slot="disclosure-trigger"], button, a, [role="button"], [data-no-disclosure-toggle="true"]',
      )
    ) {
      return;
    }

    toggleDisclosure();
  };

  const labelContent = (
    <>
      <span className="truncate font-semibold">{name}</span>
      {title && <span className="truncate">{title}</span>}
    </>
  );

  const triggerContent = (
    <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
      <div className="relative flex size-5 shrink-0 items-center justify-center">
        {canToggleDisclosure ? (
          <>
            <span className={shouldShowDisclosureAffordance ? "hidden" : undefined}>{icon}</span>
            <div
              className={`${shouldShowDisclosureAffordance ? "flex" : "hidden"} size-full items-center justify-center`}
            >
              <ChevronsCollapseVerticalIcon className="hidden size-3 group-aria-expanded/trigger:block" />
              <ChevronsExpandVerticalIcon className="block size-3 group-aria-expanded/trigger:hidden" />
            </div>
          </>
        ) : (
          <div className="flex size-full items-center justify-center">{icon}</div>
        )}
      </div>

      {shouldShimmerToolLabel ? (
        <TextShimmer className="flex min-w-0 items-center gap-2">{labelContent}</TextShimmer>
      ) : (
        labelContent
      )}
    </div>
  );

  const navigationButtonClassName =
    "hover:bg-foreground/10 active:bg-foreground/15 focus-visible:ring-accent/40 size-6 min-w-0 shrink-0 rounded-md focus-visible:ring-2";

  const headerActions = (
    <div className="flex shrink-0 items-center justify-end gap-2 text-sm">
      {navigationAction && !shouldHideNavigationAction && (
        <span
          data-no-disclosure-toggle="true"
          onMouseEnter={() => setIsNavigationActionHovered(true)}
          onMouseLeave={() => setIsNavigationActionHovered(false)}
        >
          <Tooltip delay={0}>
            <Tooltip.Trigger>
              <Button
                isIconOnly
                aria-label={navigationAction.label}
                size="sm"
                variant="ghost"
                className={navigationButtonClassName}
                onPress={navigationAction.onPress}
              >
                <ArrowUpRightFromSquareIcon className="size-3" />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Typography>{navigationAction.label}</Typography>
            </Tooltip.Content>
          </Tooltip>
        </span>
      )}

      {resultLabel && <span className="truncate text-xs">{resultLabel}</span>}

      <div className="flex shrink-0 items-center justify-center p-1">
        <StatusDot severity={derivedSeverity} />
      </div>
    </div>
  );

  const approvalContent = isPendingApproval ? (
    <ChatTool.Approval className="border-warning/30 bg-warning/5 mx-1 mb-1 rounded-lg border p-2">
      <ChatTool.ApprovalActions className="justify-end">
        {approval?.onReject && (
          <ChatTool.Reject size="sm" variant="danger-soft" onPress={approval.onReject}>
            <XmarkIcon className="size-4" />
            {approval.rejectLabel ?? "Reject"}
          </ChatTool.Reject>
        )}
        {approval?.onApprove && (
          <ChatTool.Approve size="sm" onPress={approval.onApprove}>
            <CheckIcon className="size-4" />
            {approval.approveLabel ?? "Approve"}
          </ChatTool.Approve>
        )}
      </ChatTool.ApprovalActions>
    </ChatTool.Approval>
  ) : null;

  const errorContent = errorText ? (
    <ChatTool.Error className="px-2 pb-2" errorText={errorText} />
  ) : null;

  const actionsFooter = hasActions ? (
    <div className="border-divider my-2 flex flex-wrap justify-end gap-2 border-t pt-3">
      {actions?.map((action, i) => (
        <Button key={i} size="sm" variant="secondary" onPress={action.onPress}>
          {action.label}
        </Button>
      ))}
    </div>
  ) : null;

  return (
    <ChatTool.Root
      state={heroState}
      toolName={typeof name === "string" ? name : undefined}
      isExpandable={isExpandable}
      isExpanded={resolvedExpanded}
      onExpandedChange={handleExpandedChange}
    >
      <Disclosure.Heading
        className={`group/header hover:bg-default flex w-full items-center pr-3 transition-colors ${canToggleDisclosure ? "cursor-pointer" : ""}`}
        onClick={handleHeaderClick}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => {
          setIsHeaderHovered(false);
          setIsNavigationActionHovered(false);
        }}
      >
        <Disclosure.Trigger
          className={`group/trigger chat-tool__trigger flex min-w-0 flex-1 items-center gap-2 overflow-hidden bg-transparent py-2 pr-2 pl-3 text-sm outline-none hover:bg-transparent data-[hovered=true]:bg-transparent data-[pressed=true]:bg-transparent ${!canToggleDisclosure ? "cursor-default" : ""}`}
          isDisabled={!canToggleDisclosure}
        >
          {triggerContent}
        </Disclosure.Trigger>
        {headerActions}
      </Disclosure.Heading>
      {isExpandable && (
        <ChatTool.Content>
          {children}
          {approvalContent}
          {errorContent}
          {actionsFooter}
        </ChatTool.Content>
      )}
    </ChatTool.Root>
  );
}
